import { differenceInCalendarDays, format, isValid, parseISO, startOfDay } from 'date-fns';

/** Stalled-on-client threshold (presentation filter; not a product-definition NFR). */
export const STALLED_CLIENT_DAYS = 7;

export const STATUS_OPTIONS = [
  { value: 'To do', label: 'To do' },
  { value: 'In progress', label: 'In progress' },
  { value: 'Waiting on client', label: 'Waiting on client' },
  { value: 'Marked complete', label: 'Marked complete' },
  { value: 'Done', label: 'Done' },
  { value: 'Cancelled', label: 'Cancelled' },
];

export const PRIORITY_OPTIONS = [
  { value: 'high', label: 'High' },
  { value: 'med', label: 'Med' },
  { value: 'low', label: 'Low' },
];

export const QUEUE_TABS = [
  { id: 'me', label: 'Waiting on me' },
  { id: 'us', label: 'Waiting on us' },
  { id: 'clients', label: 'Waiting on clients' },
  { id: 'unallocated', label: 'Unallocated' },
  { id: 'all', label: 'All open' },
];

export const FOCUS_FILTERS = [
  { id: 'overdue', label: 'Overdue' },
  { id: 'today', label: 'Due today' },
  { id: 'week', label: 'Due this week' },
  { id: 'me', label: 'Waiting on me' },
  { id: 'unallocated', label: 'Unallocated' },
  { id: 'needs_confirm', label: 'Needs confirm' },
  { id: 'chase_approve', label: 'Chase to approve' },
  { id: 'stalled', label: 'Stalled on client' },
];

export const SORT_OPTIONS = [
  { id: 'due', label: 'Due date' },
  { id: 'priority', label: 'Priority' },
  { id: 'updated', label: 'Updated' },
];

const PRIORITY_RANK = { high: 0, med: 1, low: 2 };

/**
 * Published + open/active/waiting (R-7702). Excludes drafts and terminal statuses.
 * @param {object} item
 * @returns {boolean}
 */
export const isPublishedOpen = (item) => {
  if (!item) return false;
  if (item.visibilityState === 'draft') return false;
  const semantic = item.statusSemantic;
  if (semantic === 'resolved' || semantic === 'cancelled') return false;
  if (item.status === 'Done' || item.status === 'Cancelled') return false;
  return true;
};

/** YYYY-MM-DD today in local calendar. */
export const todayYmd = () => format(startOfDay(new Date()), 'yyyy-MM-dd');

/**
 * @param {string|null|undefined} dueDate
 * @returns {Date|null}
 */
export const parseDueDate = (dueDate) => {
  if (!dueDate) return null;
  const d = parseISO(String(dueDate).slice(0, 10));
  return isValid(d) ? d : null;
};

/**
 * Display YYYY-MM-DD as dd/MM/yyyy (en-GB).
 * @param {string|Date|null|undefined} value
 * @returns {string}
 */
export const formatEntryDate = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : parseISO(String(value).slice(0, 10));
  if (!isValid(date)) return String(value);
  return format(date, 'dd/MM/yyyy');
};

/**
 * Relative due label for list urgency.
 * @param {string|null|undefined} dueDate
 * @returns {{ label: string, tone: 'overdue'|'today'|'soon'|'none'|'normal' }}
 */
export const dueUrgency = (dueDate) => {
  const d = parseDueDate(dueDate);
  if (!d) return { label: 'No due date', tone: 'none' };
  const today = startOfDay(new Date());
  const days = differenceInCalendarDays(d, today);
  if (days < 0) return { label: `${Math.abs(days)}d late`, tone: 'overdue' };
  if (days === 0) return { label: 'Today', tone: 'today' };
  if (days <= 7) return { label: formatEntryDate(dueDate), tone: 'soon' };
  return { label: formatEntryDate(dueDate), tone: 'normal' };
};

/**
 * Last client-visible activity timestamp (ISO) for stalled heuristic.
 * @param {object} item
 * @returns {string|null}
 */
export const lastClientVisibleAt = (item) => {
  const rows = Array.isArray(item?.timeline) ? item.timeline : [];
  let best = null;
  for (const e of rows) {
    if (!e?.at) continue;
    const isClientMsg =
      (e.kind === 'message' || e.kind === 'file_uploaded') && !e.internal && e.actorParty === 'responding';
    const isNudge = e.kind === 'nudge_sent';
    const isHolderFlip =
      e.kind === 'status_changed' &&
      (e.to === 'Waiting on client' || item.taskHolder === 'responding');
    if (isClientMsg || isNudge || isHolderFlip) {
      if (!best || String(e.at) > String(best)) best = e.at;
    }
  }
  // Fall back to when it became waiting on client / updated
  if (!best && item?.taskHolder === 'responding') {
    best = item.updatedAt || item.createdAt || null;
  }
  return best;
};

/**
 * Waiting on client with no client-visible activity for STALLED_CLIENT_DAYS.
 * @param {object} item
 * @returns {boolean}
 */
export const isStalledOnClient = (item) => {
  if (!isPublishedOpen(item)) return false;
  if (item.taskHolder !== 'responding' && item.taskHolder !== 'third_party') return false;
  const at = lastClientVisibleAt(item);
  if (!at) return true;
  const d = parseISO(String(at));
  if (!isValid(d)) return false;
  return differenceInCalendarDays(startOfDay(new Date()), startOfDay(d)) >= STALLED_CLIENT_DAYS;
};

/**
 * Quiet days label when stalled.
 * @param {object} item
 * @returns {number|null}
 */
export const quietDays = (item) => {
  if (!isStalledOnClient(item)) return null;
  const at = lastClientVisibleAt(item);
  if (!at) return STALLED_CLIENT_DAYS;
  const d = parseISO(String(at));
  if (!isValid(d)) return STALLED_CLIENT_DAYS;
  return differenceInCalendarDays(startOfDay(new Date()), startOfDay(d));
};

/**
 * Normalize current-user id argument (string or string[]).
 * @param {string|string[]|null|undefined} myUserIds
 * @returns {string[]}
 */
const asIdList = (myUserIds) => {
  if (Array.isArray(myUserIds)) return myUserIds.filter(Boolean).map(String);
  if (myUserIds) return [String(myUserIds)];
  return [];
};

/**
 * @param {object} item
 * @param {string|string[]|null|undefined} myUserIds
 * @returns {boolean}
 */
export const isAssignedToMe = (item, myUserIds) => {
  const ids = asIdList(myUserIds);
  if (!item?.assigneeUserId || !ids.length) return false;
  const assignee = String(item.assigneeUserId);
  return ids.some((id) => id === assignee);
};

/**
 * R-7702 queue membership (aligned with BE getQueues).
 * @param {object} item
 * @param {'me'|'us'|'clients'|'unallocated'|'all'} tab
 * @param {string|string[]|null} myUserIds — Cognito sub / cognitoId (and aliases)
 * @returns {boolean}
 */
export const inQueueTab = (item, tab, myUserIds) => {
  if (!isPublishedOpen(item)) return false;
  // Match BE getQueues / R-7702 (statusSemantic + taskHolder only).
  const waitingOnUs =
    item.taskHolder === 'requesting' &&
    (item.statusSemantic === 'open' || item.statusSemantic === 'active');
  const waitingOnClients =
    (item.taskHolder === 'responding' || item.taskHolder === 'third_party') &&
    (item.statusSemantic === 'active' || item.statusSemantic === 'waiting_external');

  if (tab === 'all') return true;
  if (tab === 'unallocated') return !item.assigneeUserId;
  if (tab === 'clients') return waitingOnClients;
  if (tab === 'us') return waitingOnUs;
  if (tab === 'me') {
    return waitingOnUs && isAssignedToMe(item, myUserIds);
  }
  return true;
};

/**
 * @param {object} item
 * @param {string|null} focusId
 * @param {string|string[]|null} myUserIds
 * @returns {boolean}
 */
export const matchesFocusFilter = (item, focusId, myUserIds) => {
  if (!focusId) return true;
  if (!isPublishedOpen(item)) return false;
  const today = todayYmd();
  const due = item.dueDate ? String(item.dueDate).slice(0, 10) : null;
  switch (focusId) {
    case 'overdue':
      return !!due && due < today;
    case 'today':
      return due === today;
    case 'week': {
      if (!due || due < today) return false;
      const d = parseDueDate(due);
      if (!d) return false;
      const days = differenceInCalendarDays(d, startOfDay(new Date()));
      return days >= 0 && days <= 7;
    }
    case 'me':
      return inQueueTab(item, 'me', myUserIds);
    case 'unallocated':
      return !item.assigneeUserId;
    case 'needs_confirm':
      return item.status === 'Marked complete';
    case 'chase_approve':
      return (
        (item.chasePendingCount || 0) > 0 ||
        (item.draftMessages || []).some((d) => d.status === 'pending_approval')
      );
    case 'stalled':
      return isStalledOnClient(item);
    default:
      return true;
  }
};

/**
 * @param {object[]} items
 * @param {string|null} profileSub
 * @returns {Record<string, number>}
 */
export const computeFocusCounts = (items, profileSub) => {
  const open = (items || []).filter(isPublishedOpen);
  const counts = {};
  FOCUS_FILTERS.forEach((f) => {
    counts[f.id] = open.filter((i) => matchesFocusFilter(i, f.id, profileSub)).length;
  });
  return counts;
};

/**
 * @param {object[]} items
 * @param {string|null} profileSub
 * @returns {Record<string, number>}
 */
export const computeQueueCounts = (items, profileSub) => {
  const counts = {};
  QUEUE_TABS.forEach((t) => {
    counts[t.id] = (items || []).filter((i) => inQueueTab(i, t.id, profileSub)).length;
  });
  return counts;
};

/**
 * @param {object[]} items
 * @param {'due'|'priority'|'updated'} sortBy
 * @returns {object[]}
 */
export const sortWorkItems = (items, sortBy = 'due') => {
  const list = [...(items || [])];
  list.sort((a, b) => {
    if (sortBy === 'priority') {
      const ap = PRIORITY_RANK[a.priority] ?? 9;
      const bp = PRIORITY_RANK[b.priority] ?? 9;
      if (ap !== bp) return ap - bp;
    }
    if (sortBy === 'updated') {
      const au = String(a.updatedAt || a.createdAt || '');
      const bu = String(b.updatedAt || b.createdAt || '');
      if (au !== bu) return au > bu ? -1 : 1;
    }
    const ad = a.dueDate || '9999-12-31';
    const bd = b.dueDate || '9999-12-31';
    if (ad !== bd) return ad < bd ? -1 : 1;
    return String(a.createdAt || '') < String(b.createdAt || '') ? -1 : 1;
  });
  return list;
};

/**
 * @param {object} item
 * @returns {number}
 */
export const itemTimeMinutes = (item) => {
  if (item?.timeTotals?.totalMinutes != null) return item.timeTotals.totalMinutes;
  return (item?.timeEntries || [])
    .filter((e) => !e.removedAt)
    .reduce((s, e) => s + (e.durationMinutes || 0), 0);
};

/**
 * @param {object} item
 * @returns {number}
 */
export const itemMessageCount = (item) => {
  if (item?.messageCount != null) return item.messageCount;
  return (item?.timeline || []).filter(
    (e) => e.kind === 'message' || e.kind === 'nudge_sent' || e.kind === 'file_uploaded',
  ).length;
};

/**
 * Minutes between HH:mm start/end; null if invalid or end ≤ start.
 * @param {string} start
 * @param {string} end
 * @returns {number|null}
 */
export const minutesBetween = (start, end) => {
  const parse = (hm) => {
    if (typeof hm !== 'string') return null;
    const m = hm.trim().match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
    if (!m) return null;
    return Number(m[1]) * 60 + Number(m[2]);
  };
  const a = parse(start);
  const b = parse(end);
  if (a == null || b == null || b <= a) return null;
  return b - a;
};

/**
 * Relative updated label (minutes → hours → days).
 * @param {string|null|undefined} value
 * @returns {string}
 */
export const formatRelativeUpdated = (value) => {
  if (!value) return '—';
  const d = parseISO(String(value));
  if (!isValid(d)) return '—';
  const ms = Date.now() - d.getTime();
  if (ms < 0) return 'Just now';
  const minutes = Math.floor(ms / (60 * 1000));
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = differenceInCalendarDays(startOfDay(new Date()), startOfDay(d));
  if (days === 1) return '1d ago';
  return `${days}d ago`;
};

/**
 * Chase = approval-first reminder draft to the client (R-7704).
 * Only when shared, published, and waiting on the responding party.
 * @param {object|null|undefined} item
 * @returns {boolean}
 */
export const isChaseable = (item) =>
  !!item &&
  item.visibilityState !== 'draft' &&
  !!item.shared &&
  item.taskHolder === 'responding';

/**
 * Summarise time by person for list/detail (billing-relevant).
 * @param {object} item
 * @param {(id: string) => string} resolveName
 * @returns {{ total: number, parts: Array<{ userId: string|null, label: string, minutes: number }> }}
 */
export const summarizeTimeByUser = (item, resolveName) => {
  const entries = (item?.timeEntries || []).filter((e) => !e.removedAt);
  const byUser = new Map();
  entries.forEach((e) => {
    const key = e.userId ? String(e.userId) : '__unknown__';
    byUser.set(key, (byUser.get(key) || 0) + (e.durationMinutes || 0));
  });
  const parts = [...byUser.entries()]
    .map(([key, minutes]) => {
      const userId = key === '__unknown__' ? null : key;
      const label = userId ? resolveName?.(userId) || 'Team' : 'Unknown';
      return { userId, label, minutes };
    })
    .filter((p) => p.minutes > 0)
    .sort((a, b) => b.minutes - a.minutes);
  const total =
    item?.timeTotals?.totalMinutes != null
      ? item.timeTotals.totalMinutes
      : parts.reduce((s, p) => s + p.minutes, 0);
  return { total, parts };
};

/**
 * Short list cell for time by person, e.g. "36m · You" or "20m Alice · 16m Bob".
 * @param {object} item
 * @param {(id: string) => string} resolveName
 * @returns {string}
 */
export const formatTimeByUserCell = (item, resolveName) => {
  const { total, parts } = summarizeTimeByUser(item, resolveName);
  if (!total) return '—';
  if (!parts.length) return `${total}m`;
  if (parts.length === 1) return `${parts[0].minutes}m · ${parts[0].label}`;
  return parts.map((p) => `${p.minutes}m ${p.label}`).join(' · ');
};
